import { useState } from "react";
import { MenuIcon, CloseIcon } from "@/components/common/Icons";
import { smoothScroll } from "@/utils/smoothScroll.js";

/**
 * Navigation Component
 * Responsive navigation with mobile overlay
 */
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { href: "#ingredients", label: "Ingredients" },
    { href: "#research", label: "Research" },
    { href: "#real-stories", label: "Real Stories" },
    { href: "#science", label: "Science" },
  ];

  const ctaLink = {
    href: window.cta_link || "#",
    label: "Get Yours",
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const assetsUrl = `${window.template_root}/src/public/`;

  const handleScroll = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const targetId = href.substring(1);

      smoothScroll(targetId, 1200, 0, "main-scroll-container");
    }
  };

  return (
    <nav className="absolute top-0 left-0 right-0 z-[200] flex-shrink-0">
      <div className="grid grid-cols-[auto_1fr] items-center py-5 px-6 nav-gradient max-md:flex max-md:justify-between max-md:gap-4">
        {/* Logo */}
        <div className="flex justify-start">
          <img
            src={`${assetsUrl}img/logo.svg`}
            alt="Naori"
            className="h-6 w-auto"
          />
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex gap-3 justify-self-end">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleScroll(e, link.href)}
              className="text-white text-sm font-[480] py-3 px-5 rounded-[88rem]
                         glass transition-all hover:bg-black/15 hover:-translate-y-px"
            >
              {link.label}
            </a>
          ))}
          <a
            href={ctaLink.href}
            className="bg-white text-main text-sm font-medium py-3 px-5 rounded-[88rem]
                       transition-all hover:-translate-y-px"
          >
            {ctaLink.label}
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={toggleMenu}
          className="md:hidden flex items-center justify-center py-2 px-4 rounded-[1.2rem]
                     glass text-white transition-all"
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <CloseIcon className="w-[22px] h-4" color="white" />
          ) : (
            <MenuIcon className="w-[22px] h-4" color="white" />
          )}
        </button>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 z-[999] p-2 flex flex-col overflow-y-auto
                        bg-[#1c3025de] backdrop-blur-[50px]"
        >
          {/* Overlay Header */}
          <div className="flex items-center justify-between py-5 px-6">
            <img src={`${assetsUrl}img/logo.svg`} alt="SPA" className="h-6" />
            <button
              onClick={closeMenu}
              className="py-2 px-4 rounded-[1.2rem] bg-white/15 backdrop-blur-[10px]"
              aria-label="Close navigation"
            >
              <CloseIcon className="w-[22px] h-4" color="white" />
            </button>
          </div>

          {/* Overlay Links */}
          <div className="flex flex-col gap-6 text-2xl font-[480] py-8 px-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  handleScroll(e, link.href);
                  closeMenu();
                }}
                className="text-white no-underline leading-tight"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Overlay CTA */}
          <div className="mt-auto py-6 px-6">
            <a
              href={ctaLink.href}
              onClick={closeMenu}
              className="block bg-secondary text-green rounded-[88rem] w-full text-center
                         min-h-[58px] leading-[58px] font-[480] text-base"
            >
              {ctaLink.label}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navigation;
